import { Link } from "react-router-dom";
import type { PurchasePaperRef, PurchasesIvaSummary } from "../../api/types";
import { calendarDayText, money } from "../../i18n/intl";

function PaperList({ papers }: { papers: PurchasePaperRef[] }) {
  return (
    <ul className="purch-not-counted-papers">
      {papers.map((p) => (
        <li key={p.id}>
          <Link to={`/app/invoices/${p.id}`}>
            {p.supplierName} {p.invoiceNumber ?? "(no number)"}
          </Link>
          <span className="muted">
            {" "}
            · {p.invoiceDate ? calendarDayText(p.invoiceDate) : "no date"} · {p.locationName}
          </span>
          {p.coveredByFactura ? (
            <span className="muted">
              {" "}
              · counted by factura <Link to={`/app/invoices/${p.coveredByFactura.id}`}>{p.coveredByFactura.label}</Link>
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * G-121 S4: every paper of the month that the IVA-paid total did NOT use, and
 * why — so nothing is silently dropped and a fixable one can be fixed.
 * Folded by default: it explains the total, it is not the total.
 */
export function PurchasesNotCounted({ notCounted }: { notCounted: PurchasesIvaSummary["notCounted"] }) {
  const n = notCounted;
  const total =
    n.albaranes.count +
    n.itemsOnly.count +
    n.copies.length +
    n.notRead.length +
    n.missingDateOrNumber.length +
    n.ivaNotRead.length +
    n.wrongSign.length;
  if (total === 0) return null;
  const waitingAlbaranes = n.albaranes.rows.filter((p) => p.coveredByFactura === null);
  const countedAlbaranes = n.albaranes.rows.filter((p) => p.coveredByFactura !== null);

  const groups: Array<{ key: string; title: string; why: string; papers: PurchasePaperRef[] }> = [
    {
      key: "copies",
      title: `Copies: ${n.copies.length}`,
      why: "The same factura uploaded again — counted once, on the first upload.",
      papers: n.copies,
    },
    {
      key: "not-read",
      title: `Not read: ${n.notRead.length}`,
      why: "Still being read, or the reader could not read it.",
      papers: n.notRead,
    },
    {
      key: "no-date",
      title: `No date or number: ${n.missingDateOrNumber.length}`,
      why: "The reader found no date to put it in a month, or no number to tell it from a copy.",
      papers: n.missingDateOrNumber,
    },
    {
      key: "no-iva",
      title: `IVA not read: ${n.ivaNotRead.length}`,
      why: "Read, but the reader found no IVA on it.",
      papers: n.ivaNotRead,
    },
    {
      key: "sign",
      title: `Sign does not match the type: ${n.wrongSign.length}`,
      why: "A factura with negative amounts (change it to Credit note), or a credit note above zero (type its amounts).",
      papers: n.wrongSign,
    },
  ];

  return (
    <details className="purch-not-counted">
      <summary>Not counted this month: {total}</summary>
      {n.albaranes.count > 0 ? (
        <div className="purch-not-counted-group">
          <strong>Albaranes: {n.albaranes.count}</strong>{" "}
          <span className="muted">
            ({money(n.albaranes.baseEur)} before IVA) — delivery papers: their prices are the stock cost; the
            IVA paid is on the factura.
          </span>
          {/* G-131: the ones still waiting first, then the ones a factura counts. */}
          {waitingAlbaranes.length > 0 ? (
            <>
              <div className="muted">Waiting for a factura: {waitingAlbaranes.length}</div>
              <PaperList papers={waitingAlbaranes} />
            </>
          ) : null}
          {countedAlbaranes.length > 0 ? (
            <>
              <div className="muted">Counted by their factura: {countedAlbaranes.length}</div>
              <PaperList papers={countedAlbaranes} />
            </>
          ) : null}
        </div>
      ) : null}
      {n.itemsOnly.count > 0 ? (
        <div className="purch-not-counted-group">
          <strong>Uploaded only to create items: {n.itemsOnly.count}</strong>{" "}
          <span className="muted">— count for nothing.</span>
        </div>
      ) : null}
      {groups
        .filter((g) => g.papers.length > 0)
        .map((g) => (
          <div key={g.key} className="purch-not-counted-group">
            <strong>{g.title}</strong> <span className="muted">— {g.why}</span>
            <PaperList papers={g.papers} />
          </div>
        ))}
    </details>
  );
}
